// Automation Solution Sets — the automations a scenario deploys now arrive as a
// saved set (sent in by another application) rather than living on the model.
// Applying a set copies its automations and timings onto the scenario profile;
// everything downstream reads `scenarioAutomations(profile)`.

import { LEVEL_IDS } from '../types.js';
import type {
  AutomationDeploymentSetting,
  AutomationImpactRow,
  AutomationSolution,
  ClientProfile,
  LevelId,
} from '../types.js';

/** The automations a scenario deploys — the copy taken from its applied set. */
export function scenarioAutomations(
  profile: Pick<ClientProfile, 'automations'> | undefined,
): AutomationImpactRow[] {
  return (profile?.automations || []).filter(Boolean);
}

/** Profile fields to set when a scenario applies `solution`. */
export function automationSolutionProfilePatch(
  solution: AutomationSolution,
): Partial<ClientProfile> {
  return {
    currentAutomationSolutionId: solution.id,
    currentAutomationSolutionUpdatedAt: solution.updatedAt,
    automations: (solution.automations || []).map((a) => ({
      ...a,
      disciplineChannelKeys: [...(a.disciplineChannelKeys || [])],
      levelIds: [...(a.levelIds || [])],
      subPhaseIds: [...(a.subPhaseIds || [])],
    })),
    automationDeployments: Object.fromEntries(
      Object.entries(solution.deployments || {}).map(([id, d]) => [id, { ...d }]),
    ),
    ...(solution.automationBasis ? { automationBasis: solution.automationBasis } : {}),
  };
}

/** Profile fields to clear when a scenario detaches its set. */
export function clearedAutomationSolutionPatch(): Partial<ClientProfile> {
  return {
    currentAutomationSolutionId: undefined,
    currentAutomationSolutionUpdatedAt: undefined,
    automations: undefined,
    automationDeployments: undefined,
  };
}

export interface NormalizedAutomationSet {
  automations: AutomationImpactRow[];
  deployments: Record<string, AutomationDeploymentSetting>;
  warnings: string[];
}

function strings(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const trimmed = v
    .filter((x): x is string => typeof x === 'string')
    .map((x) => x.trim())
    .filter(Boolean);
  return Array.from(new Set(trimmed));
}

/** Reads 0.3, 30 and "30%" all as a 30% reduction. */
function fraction(v: unknown): number | null {
  let n: number;
  if (typeof v === 'string') {
    const t = v.trim();
    n = Number(t.endsWith('%') ? t.slice(0, -1) : t);
    if (t.endsWith('%')) n /= 100;
  } else if (typeof v === 'number') {
    n = v;
  } else {
    return null;
  }
  if (!isFinite(n)) return null;
  if (n > 1) n /= 100;
  return Math.min(1, Math.max(0, n));
}

function deployment(v: unknown): AutomationDeploymentSetting | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  const out: AutomationDeploymentSetting = {};
  if (typeof r.startMonth === 'number' && isFinite(r.startMonth)) out.startMonth = r.startMonth;
  if (typeof r.rampMonths === 'number' && isFinite(r.rampMonths)) out.rampMonths = r.rampMonths;
  if (typeof r.enabled === 'boolean') out.enabled = r.enabled;
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Validates the automations and deployments of an incoming set. Rows without
 * an id or a usable reduction are dropped; unknown levels are removed; ids
 * are de-duplicated (first wins). Deployments for unknown ids are dropped.
 */
export function normalizeAutomationSet(
  rawAutomations: unknown,
  rawDeployments: unknown,
): NormalizedAutomationSet {
  const warnings: string[] = [];
  const automations: AutomationImpactRow[] = [];
  const seen = new Set<string>();
  const rows = Array.isArray(rawAutomations) ? rawAutomations : [];
  if (rawAutomations != null && !Array.isArray(rawAutomations)) {
    warnings.push('automations must be an array; ignored.');
  }

  rows.forEach((raw, i) => {
    if (!raw || typeof raw !== 'object') {
      warnings.push(`automations[${i}] is not an object; dropped.`);
      return;
    }
    const r = raw as Record<string, unknown>;
    const id = typeof r.id === 'string' ? r.id.trim() : '';
    if (!id) {
      warnings.push(`automations[${i}] has no id; dropped.`);
      return;
    }
    if (seen.has(id)) {
      warnings.push(`automations[${i}] repeats id "${id}"; dropped.`);
      return;
    }
    const reductionPct = fraction(r.reductionPct);
    if (reductionPct === null) {
      warnings.push(`Automation "${id}" has no numeric reductionPct; dropped.`);
      return;
    }
    const rawLevels = strings(r.levelIds);
    const levelIds = rawLevels.filter((l): l is LevelId =>
      (LEVEL_IDS as readonly string[]).includes(l),
    );
    if (levelIds.length < rawLevels.length) {
      warnings.push(`Automation "${id}" had unknown level ids; removed.`);
    }
    seen.add(id);
    const row: AutomationImpactRow = {
      id,
      label: typeof r.label === 'string' && r.label.trim() ? r.label.trim() : id,
      reductionPct,
      disciplineChannelKeys: strings(r.disciplineChannelKeys),
      levelIds,
      subPhaseIds: strings(r.subPhaseIds),
    };
    if (typeof r.description === 'string' && r.description.trim()) {
      row.description = r.description.trim();
    }
    if (typeof r.active === 'boolean') row.active = r.active;
    automations.push(row);
  });

  const deployments: Record<string, AutomationDeploymentSetting> = {};
  if (rawDeployments && typeof rawDeployments === 'object' && !Array.isArray(rawDeployments)) {
    for (const [id, v] of Object.entries(rawDeployments as Record<string, unknown>)) {
      if (!seen.has(id)) {
        warnings.push(`Deployment for unknown automation "${id}"; dropped.`);
        continue;
      }
      const d = deployment(v);
      if (d) deployments[id] = d;
    }
  }

  return { automations, deployments, warnings };
}
